import { supabase } from "@/integrations/supabase/client";
import {
  CreateVoiceFromPreviewRequest,
  InstantVoiceCloneRequest,
  InstantVoiceCloneResponse,
  PvcCaptchaResponse,
  PvcCreateRequest,
  PvcCreateResponse,
  PvcSample,
  PvcSampleUpdateRequest,
  PvcSampleUploadRequest,
  PvcSpeakerStatus,
  PvcStatusResponse,
  PvcTrainRequest,
  VoiceDesignRequest,
  VoicePreviewResponse,
  VoiceRemixRequest,
} from "@/types/voice/voice-creation";

export class VoiceCreationService {
  private static instance: VoiceCreationService;

  private constructor() {}

  public static getInstance(): VoiceCreationService {
    if (!VoiceCreationService.instance) {
      VoiceCreationService.instance = new VoiceCreationService();
    }
    return VoiceCreationService.instance;
  }

  private async invokeJson<T>(operation: string, data?: Record<string, unknown>): Promise<T> {
    const token = await this.getAccessToken();
    const { data: response, error } = await supabase.functions.invoke("elevenlabs", {
      headers: {
        authorization: `Bearer ${token}`,
      },
      body: {
        resource: "voices",
        action: "create",
        operation,
        params: data,
      },
    });

    if (error) {
      console.error(`ElevenLabs voice create (${operation}) failed:`, error);
      throw new Error(error.message || "ElevenLabs voice creation failed");
    }

    return response as T;
  }

  private async invokeMultipart<T>(operation: string, formData: FormData): Promise<T> {
    const token = await this.getAccessToken();
    formData.append("resource", "voices");
    formData.append("action", "create");
    formData.append("operation", operation);

    const { data, error } = await supabase.functions.invoke("elevenlabs", {
      headers: {
        authorization: `Bearer ${token}`,
      },
      body: formData,
    });

    if (error) {
      console.error(`ElevenLabs voice create (${operation}) failed:`, error);
      throw new Error(error.message || "ElevenLabs voice creation failed");
    }

    return data as T;
  }

  private async getAccessToken(): Promise<string> {
    const { data: { session }, error } = await supabase.auth.getSession();
    if (error || !session?.access_token) {
      throw new Error("Failed to get access token");
    }
    return session.access_token;
  }

  public designVoice(payload: VoiceDesignRequest): Promise<VoicePreviewResponse> {
    return this.invokeJson("design", payload as unknown as Record<string, unknown>);
  }

  public createVoiceFromPreview(payload: CreateVoiceFromPreviewRequest) {
    return this.invokeJson("create-from-preview", payload as unknown as Record<string, unknown>);
  }

  public remixVoice(payload: VoiceRemixRequest): Promise<VoicePreviewResponse> {
    return this.invokeJson("remix", payload as unknown as Record<string, unknown>);
  }

  public async createInstantVoiceClone(payload: InstantVoiceCloneRequest): Promise<InstantVoiceCloneResponse> {
    const formData = new FormData();
    formData.append("name", payload.name);
    payload.files.forEach((file) => formData.append("files", file));

    if (payload.remove_background_noise !== undefined) {
      formData.append("remove_background_noise", String(payload.remove_background_noise));
    }
    if (payload.description) {
      formData.append("description", payload.description);
    }
    if (payload.labels) {
      formData.append("labels", JSON.stringify(payload.labels));
    }

    return this.invokeMultipart("ivc", formData);
  }

  public createPvc(payload: PvcCreateRequest): Promise<PvcCreateResponse> {
    return this.invokeJson("pvc-create", payload as unknown as Record<string, unknown>);
  }

  public async uploadPvcSamples(payload: PvcSampleUploadRequest): Promise<PvcSample[]> {
    const formData = new FormData();
    formData.append("voice_id", payload.voice_id);
    payload.files.forEach((file) => formData.append("files", file));
    if (payload.remove_background_noise !== undefined) {
      formData.append("remove_background_noise", String(payload.remove_background_noise));
    }
    return this.invokeMultipart("pvc-upload-samples", formData);
  }

  public separatePvcSpeakers(voiceId: string, sampleId: string) {
    return this.invokeJson("pvc-separate-speakers", { voice_id: voiceId, sample_id: sampleId });
  }

  public getPvcSpeakerStatus(voiceId: string, sampleId: string): Promise<PvcSpeakerStatus> {
    return this.invokeJson("pvc-speaker-status", { voice_id: voiceId, sample_id: sampleId });
  }

  public getPvcSpeakerAudio(voiceId: string, sampleId: string, speakerId: string) {
    return this.invokeJson("pvc-speaker-audio", {
      voice_id: voiceId,
      sample_id: sampleId,
      speaker_id: speakerId,
    });
  }

  public getPvcSampleAudio(voiceId: string, sampleId: string, removeBackgroundNoise?: boolean) {
    return this.invokeJson("pvc-sample-audio", {
      voice_id: voiceId,
      sample_id: sampleId,
      remove_background_noise: removeBackgroundNoise,
    });
  }

  public updatePvcSample(payload: PvcSampleUpdateRequest) {
    return this.invokeJson("pvc-sample-update", payload as unknown as Record<string, unknown>);
  }

  public getPvcCaptcha(voiceId: string): Promise<PvcCaptchaResponse> {
    return this.invokeJson("pvc-captcha", { voice_id: voiceId });
  }

  public async verifyPvcCaptcha(voiceId: string, recording: File) {
    const formData = new FormData();
    formData.append("voice_id", voiceId);
    formData.append("recording", recording);
    return this.invokeMultipart("pvc-captcha-verify", formData);
  }

  public async requestPvcManualVerification(voiceId: string, files: File[], extraText?: string) {
    const formData = new FormData();
    formData.append("voice_id", voiceId);
    files.forEach((file) => formData.append("files", file));
    if (extraText) {
      formData.append("extra_text", extraText);
    }
    return this.invokeMultipart("pvc-manual-verification", formData);
  }

  public trainPvc(payload: PvcTrainRequest) {
    return this.invokeJson("pvc-train", payload as unknown as Record<string, unknown>);
  }

  public getPvcStatus(voiceId: string): Promise<PvcStatusResponse> {
    return this.invokeJson("pvc-status", { voice_id: voiceId });
  }
}
